import React, { useEffect, useState } from "react"
import { useKBar } from "kbar"
import styled from "styled-components"
import { uiColorToBaseColor } from "styleguide/styles/colors"
import media from "styleguide/styles/media-queries"

const TOOLTIP_STORAGE_KEY = "kbar-tooltip-dismissed"

// Desktop keyboard shortcut badge
const DesktopBadge = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  background: transparent;
  border: 1px solid ${uiColorToBaseColor("lightGray")};
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  color: ${uiColorToBaseColor("darkGray")};
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    background: ${uiColorToBaseColor("lightestGray")};
    border-color: ${uiColorToBaseColor("darkGray")};
  }

  ${media.tablet`
    display: none;
  `}
`

const KeyboardShortcut = styled.kbd`
  padding: 2px 6px;
  background: white;
  border: 1px solid ${uiColorToBaseColor("lightGray")};
  border-radius: 4px;
  font-size: 12px;
  font-family: monospace;
  color: ${uiColorToBaseColor("darkestGray")};
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
`

// Mobile search icon button
const MobileButton = styled.button`
  display: none;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: transparent;
  border: 1px solid ${uiColorToBaseColor("lightGray")};
  border-radius: 6px;
  cursor: pointer;
  color: ${uiColorToBaseColor("darkGray")};
  transition: all 0.2s ease;
  padding: 0;
  flex-shrink: 0;

  &:hover {
    background: ${uiColorToBaseColor("lightestGray")};
    border-color: ${uiColorToBaseColor("darkGray")};
  }

  svg {
    width: 18px;
    height: 18px;
    stroke: currentColor;
    stroke-width: 2;
    fill: none;
  }

  ${media.tablet`
    display: flex;
  `}
`

// First-time tooltip
const TooltipContainer = styled.div`
  position: relative;
`

const Tooltip = styled.div<{ show: boolean }>`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  background: ${uiColorToBaseColor("darkestGray")};
  color: white;
  padding: 12px 16px;
  border-radius: 6px;
  font-size: 13px;
  white-space: nowrap;
  opacity: ${(props) => (props.show ? 1 : 0)};
  visibility: ${(props) => (props.show ? "visible" : "hidden")};
  transition: opacity 0.3s ease, visibility 0.3s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;

  &::before {
    content: "";
    position: absolute;
    top: -6px;
    right: 20px;
    width: 0;
    height: 0;
    border-left: 6px solid transparent;
    border-right: 6px solid transparent;
    border-bottom: 6px solid ${uiColorToBaseColor("darkestGray")};
  }

  ${media.tablet`
    display: none;
  `}
`

const TooltipClose = styled.button`
  margin-left: 12px;
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  font-size: 16px;
  padding: 0;
  opacity: 0.7;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 1;
  }
`

function detectOS(): "mac" | "windows" {
  if (typeof window === "undefined") return "windows"
  return navigator.platform.toLowerCase().includes("mac") ? "mac" : "windows"
}

export function SearchTrigger() {
  const { query } = useKBar()
  const [os, setOS] = useState<"mac" | "windows">("windows")
  const [showTooltip, setShowTooltip] = useState(false)

  useEffect(() => {
    setOS(detectOS())

    // Check if tooltip has been dismissed
    const dismissed = localStorage.getItem(TOOLTIP_STORAGE_KEY)
    if (!dismissed) {
      // Show tooltip after a short delay
      const timer = setTimeout(() => {
        setShowTooltip(true)
      }, 1000)

      // Auto-hide after 8 seconds
      const hideTimer = setTimeout(() => {
        setShowTooltip(false)
        localStorage.setItem(TOOLTIP_STORAGE_KEY, "true")
      }, 9000)

      return () => {
        clearTimeout(timer)
        clearTimeout(hideTimer)
      }
    }

    return undefined
  }, [])

  const handleClick = () => {
    query.toggle()
    if (showTooltip) {
      setShowTooltip(false)
      localStorage.setItem(TOOLTIP_STORAGE_KEY, "true")
    }
  }

  const dismissTooltip = (e: React.MouseEvent) => {
    e.stopPropagation()
    setShowTooltip(false)
    localStorage.setItem(TOOLTIP_STORAGE_KEY, "true")
  }

  const shortcut = os === "mac" ? "⌘K" : "Ctrl+K"

  return (
    <>
      {/* Desktop badge */}
      <TooltipContainer>
        <DesktopBadge onClick={handleClick} aria-label="Search documentation">
          <span>Search</span>
          <KeyboardShortcut>{shortcut}</KeyboardShortcut>
        </DesktopBadge>
        <Tooltip show={showTooltip}>
          Press <strong>{shortcut}</strong> to search anytime
          <TooltipClose onClick={dismissTooltip}>✕</TooltipClose>
        </Tooltip>
      </TooltipContainer>

      {/* Mobile button */}
      <MobileButton onClick={handleClick} aria-label="Search documentation">
        <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <circle cx="10.5" cy="10.5" r="7.5" />
          <line x1="16" y1="16" x2="21" y2="21" strokeLinecap="round" />
        </svg>
      </MobileButton>
    </>
  )
}
